<div class="sce__header">
  <admin-breadcrumbs></admin-breadcrumbs>

  <!-- The board's headboard anatomy: identity chip, name, one line of lede,
       figures on the right. Same card, same ramp, next room. -->
  <div class="sce__head-card">
    <div class="sce__head-row">
      <span class="sce__head-chip" aria-hidden="true">✉</span>

      <div class="sce__head-id">
        <h3>Scheduled Emails</h3>
        <p class="sce__lede">Every automatic email this region sends.
          <!-- A role that cannot open the page has no list to call read-only;
               the panel below already says what it can and cannot do. -->
          <ng-container *ngIf="canOpen">
            <ng-container *ngIf="canAdminister">See who receives each one, change when it goes out, or pause it.</ng-container>
            <ng-container *ngIf="!canAdminister">This list is read-only for your role; ask an administrator to change a schedule.</ng-container>
          </ng-container>
        </p>
      </div>

      <div class="sce__figures" *ngIf="canOpen && !loading && !loadError">
        <strong>{{ rows.length }}</strong>
        <span class="sce__figures-label">{{ rows.length === 1 ? 'email' : 'emails' }}</span>
        <span class="sce__dot" aria-hidden="true">·</span>
        <strong>{{ activeCount }}</strong>
        <span class="sce__figures-label">active</span>
        <button type="button" class="sce__refresh" (click)="reload()">Refresh</button>
      </div>
    </div>
  </div>
</div>

<div class="sce__body" appFitViewport>
  <div class="sce__noaccess" *ngIf="!canOpen">
    <strong>This page is for report administrators.</strong>
    Your role can receive scheduled emails, but cannot see or change the schedules.
    Ask an administrator if something needs changing.
  </div>

  <div class="sce__loading" *ngIf="loading && canOpen">Loading scheduled emails…</div>

  <p class="sce__error" *ngIf="loadError">{{ loadError }}</p>

  <ng-container *ngIf="canOpen && !loading && !loadError">

    <p class="sce__warn" *ngIf="recipientlessCount">
      {{ recipientlessCount }} active {{ recipientlessCount === 1 ? 'email has' : 'emails have' }}
      no recipients - nothing will be delivered until someone is added.
    </p>

    <p class="sce__error" *ngIf="actionError">{{ actionError }}</p>

    <!-- Reports an admin can schedule from here while they have no schedule
         yet - singletons that carry a drawer of their own. -->
    <div class="sce__setup" *ngIf="unscheduled.length">
      <span class="sce__setup-label">Not scheduled yet</span>
      <button type="button" class="sce__setup-btn" *ngFor="let entry of unscheduled; trackBy: trackBySetup"
        (click)="setUp(entry)"
        [attr.aria-label]="'Set up the ' + entry.label + ' email'">
        {{ entry.label }}<span class="sce__setup-chev" aria-hidden="true">&rsaquo;</span>
      </button>
    </div>

    <div class="sce__empty" *ngIf="!rows.length">
      No scheduled emails yet.
      <ng-container *ngIf="unscheduled.length">Use the buttons above, or set one up from a report's own screen.</ng-container>
      <ng-container *ngIf="!unscheduled.length">Set one up from a report's own screen.</ng-container>
    </div>

    <table class="sce__table" *ngIf="rows.length">
      <thead>
        <tr>
          <th class="sce__th">Report</th>
          <th class="sce__th">Schedule</th>
          <th class="sce__th">Next send</th>
          <th class="sce__th">Last sent</th>
          <th class="sce__th sce__th--num">Recipients</th>
          <th class="sce__th sce__th--actions">Status</th>
        </tr>
      </thead>

      <tbody>
        <ng-container *ngFor="let row of rows; trackBy: trackByKey">
          <!-- The band is the thread back to the board: same six washes, keyed
               by report type so a colour always means the same report. -->
          <tr class="sce__row" [ngClass]="'sce__row--b' + bandOf(row)"
            [class.sce__row--off]="!row.enabled">
            <td class="sce__td">
              <button type="button" class="sce__name" *ngIf="canManageRow(row); else nameOnly"
                [disabled]="!!busyKey" (click)="editRow(row)">{{ nameLabel(row) }}</button>
              <ng-template #nameOnly><span class="sce__name">{{ nameLabel(row) }}</span></ng-template>
              <span class="sce__type">{{ typeLabel(row) }}</span>

              <span class="sce__missing" *ngIf="!row.exists"
                title="The schedule still exists but the report behind it does not. It will never send.">
                report missing
              </span>
            </td>

            <td class="sce__td">{{ scheduleLabel(row) }}</td>

            <td class="sce__td">{{ nextSendLabel(row) }}</td>

            <td class="sce__td">
              <span>{{ lastRunLabel(row) }}</span>
              <span class="sce__pill" *ngIf="lastRunOutcome(row)" [ngClass]="outcomeClass(row)">
                {{ lastRunOutcome(row) }}
              </span>
            </td>

            <td class="sce__td sce__td--num">
              <button type="button" class="sce__count" (click)="toggleExpand(row)"
                [attr.aria-expanded]="expandedKey === keyOf(row)"
                [class.sce__count--zero]="!row.recipients.length">
                {{ row.recipients.length }}
                <span class="sce__caret">{{ expandedKey === keyOf(row) ? '▾' : '▸' }}</span>
              </button>
            </td>

            <td class="sce__td sce__td--actions">
              <!-- busyKey is ONE lock for the whole table: the handlers refuse
                   while any row is in flight, so every row's controls must go
                   with it or the click is dropped without a word. The busy
                   wording still belongs to the row that owns the lock. -->
              <button type="button" class="sce__toggle" *ngIf="canManageRow(row)"
                [class.sce__toggle--on]="row.enabled"
                [attr.aria-pressed]="row.enabled"
                [disabled]="!!busyKey"
                (click)="setEnabled(row, !row.enabled)">
                {{ busyKey === keyOf(row) ? '…' : (row.enabled ? 'On' : 'Off') }}
              </button>
              <!-- Managed-by-admin rows: state shown, controls withheld -->
              <span class="sce__type" *ngIf="!canManageRow(row)"
                [title]="isAdminManaged(row) ? 'This email is managed by administrators only.' : 'Only administrators can change this from here.'">
                {{ row.enabled ? 'On' : 'Off' }} · {{ isAdminManaged(row) ? 'admin-managed' : 'read only' }}
              </span>
              <button type="button" class="sce__test" *ngIf="canManageRow(row)" (click)="askTest(row)"
                [disabled]="!!busyKey"
                title="Builds this report now and emails it to you only. Nobody else receives it.">
                {{ testingKey === keyOf(row) ? 'Sending…' : 'Test' }}
              </button>
              <button type="button" class="sce__edit" *ngIf="canManageRow(row)"
                [disabled]="!!busyKey" (click)="editRow(row)">Edit</button>
              <button type="button" class="sce__delete" *ngIf="canManageRow(row)" (click)="askDelete(row)"
                [disabled]="!!busyKey">Delete</button>
            </td>
          </tr>

          <tr class="sce__detail" *ngIf="confirmingTestKey === keyOf(row)">
            <td class="sce__td sce__td--confirm" colspan="6">
              <div class="sce__confirm">
                <div class="sce__confirm-text">
                  <strong>Send a test of "{{ nameLabel(row) }}" now?</strong>
                  The report is built from live data and emailed to
                  <ng-container *ngIf="myEmail"><strong>{{ myEmail }}</strong> only.</ng-container>
                  <ng-container *ngIf="!myEmail">your own account only.</ng-container>
                  Nobody else receives it, and the schedule is not affected.
                </div>
                <div class="sce__confirm-actions">
                  <button type="button" class="sce__primary" [disabled]="!!busyKey"
                    (click)="sendTest(row)">Send test to me</button>
                  <button type="button" class="sce__cancel" (click)="cancelTest()">Cancel</button>
                </div>
              </div>
            </td>
          </tr>

          <tr class="sce__detail" *ngIf="testResult && testResult.key === keyOf(row)">
            <td class="sce__td sce__td--detail" colspan="6">
              <span class="sce__test-result" [class.sce__test-result--bad]="!testResult.ok">
                {{ testResult.ok ? '✓' : '✕' }} {{ testResult.message }}
              </span>
            </td>
          </tr>

          <tr class="sce__detail" *ngIf="confirmingKey === keyOf(row)">
            <td class="sce__td sce__td--confirm" colspan="6">
              <div class="sce__confirm">
                <div class="sce__confirm-text">
                  <strong>Delete "{{ nameLabel(row) }}"?</strong>
                  This stops the email and removes
                  {{ row.recipients.length }} {{ row.recipients.length === 1 ? 'recipient' : 'recipients' }}.
                  The report itself stays; you can schedule it again later.
                  Past send history is kept.
                  <span class="sce__confirm-warn" *ngIf="isReseeded(row)">
                    Note: the server re-creates this particular schedule on every restart.
                    To stop it for good, switch it to Off instead.
                  </span>
                  <label class="sce__confirm-hint" [attr.for]="'sce-del-' + keyOf(row)">
                    To confirm, type the report's name: <strong>{{ nameLabel(row) }}</strong>
                  </label>
                  <input class="sce__confirm-input" type="text" autocomplete="off" spellcheck="false" #deleteName
                    [id]="'sce-del-' + keyOf(row)" [value]="deleteTyped"
                    (input)="onDeleteTyped(deleteName.value)"
                    [attr.aria-invalid]="deleteTyped && !deleteConfirmed(row) ? true : null"
                    [attr.aria-describedby]="deleteTyped && !deleteConfirmed(row) ? 'sce-del-warn-' + keyOf(row) : null"
                    placeholder="Report name">
                  <span class="sce__confirm-warn" role="alert" [id]="'sce-del-warn-' + keyOf(row)"
                    *ngIf="deleteTyped && !deleteConfirmed(row)">That doesn't match "{{ nameLabel(row) }}".</span>
                </div>
                <div class="sce__confirm-actions">
                  <button type="button" class="sce__danger" [disabled]="!!busyKey || !deleteConfirmed(row)"
                    (click)="confirmDelete(row)">
                    {{ busyKey === keyOf(row) ? 'Deleting…' : 'Delete' }}
                  </button>
                  <button type="button" class="sce__cancel" (click)="cancelDelete()">Cancel</button>
                </div>
              </div>
            </td>
          </tr>

          <tr class="sce__detail" *ngIf="expandedKey === keyOf(row)">
            <td class="sce__td sce__td--detail" colspan="6">
              <div *ngIf="!row.recipients.length" class="sce__muted">
                Nobody is subscribed to this email yet.
                <button type="button" class="sce__inline-link" *ngIf="canManageRow(row)"
                  [disabled]="!!busyKey" (click)="editRow(row)">Add recipients</button>
              </div>

              <ul class="sce__recipients" *ngIf="row.recipients.length">
                <li *ngFor="let person of row.recipients">
                  <span class="sce__person">{{ person.privateName || person.email }}</span>
                  <span class="sce__email">{{ person.email }}</span>
                </li>
              </ul>

              <p class="sce__meta" *ngIf="row.updatedAt">
                Last changed by {{ row.updatedBy || 'unknown' }}
              </p>
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>

  </ng-container>
</div>

<!-- The editor, in place over the table: schedule + recipients for every
     report, plus the High Risk content cards when the viewer is an admin. -->
<app-high-risk-content-drawer
  [open]="!!editing"
  [includeSchedule]="true"
  [reportId]="editing?.reportId || ''"
  [reportTypeWire]="editing?.reportType || ''"
  [reportLabel]="editing ? nameLabel(editing) : ''"
  [band]="editing ? bandOf(editing) : 6"
  [loadOptions]="editingOptionsLoader"
  [saveOptions]="editingOptionsSaver"
  (saved)="onEditorSaved()"
  (saveFailed)="onEditorSaveFailed($event)"
  (closed)="onEditorClosed()">
</app-high-risk-content-drawer>
